import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RootState } from '../store'
import { DocumentRecord } from '../../models/Document'
import { User } from '../../models/User'

interface DocumentBatchState {
  isLoading: boolean
  pollingEnabled: boolean
  documents: DocumentRecord[]
  creator: User | null
  creatorIsLoading: boolean
  contact: User | null
  contactIsLoading: boolean
  chatResultIsLoading: boolean
  chatResultLoadFailed: boolean
  chatAiAgentId: string | null
  chatInteractionIsLoading: boolean
}

const initialState: DocumentBatchState = {
  isLoading: true,
  pollingEnabled: false,
  documents: [],
  creator: null,
  creatorIsLoading: false,
  contact: null,
  contactIsLoading: true,
  chatResultIsLoading: true,
  chatResultLoadFailed: false,
  chatAiAgentId: null,
  chatInteractionIsLoading: false,
}

export const documentBatchSlice = createSlice({
  name: 'documentBatch',
  initialState,
  reducers: {
    setDocumentBatchLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setDocumentBatchDocuments: (state, action: PayloadAction<DocumentRecord[]>) => {
      state.documents = action.payload
    },
    startDocumentBatchPolling: (state) => {
      state.pollingEnabled = true
    },
    stopDocumentBatchPolling: (state) => {
      state.pollingEnabled = false
    },
    setDocumentBatchCreator: (state, action: PayloadAction<User>) => {
      state.creator = action.payload
    },
    setDocumentBatchCreatorIsLoading: (state, action: PayloadAction<boolean>) => {
      state.creatorIsLoading = action.payload
    },
    setDocumentBatchContact: (state, action: PayloadAction<User>) => {
      state.contact = action.payload
    },
    setDocumentBatchContactIsLoading: (state, action: PayloadAction<boolean>) => {
      state.contactIsLoading = action.payload
    },
    setDocumentBatchChatResultLoading: (state, action: PayloadAction<boolean>) => {
      state.chatResultIsLoading = action.payload
    },
    setDocumentBatchChatResultLoadFailed: (state, action: PayloadAction<boolean>) => {
      state.chatResultLoadFailed = action.payload
    },
    setDocumentBatchChatAiAgentId: (state, action: PayloadAction<string>) => {
      state.chatAiAgentId = action.payload
    },
    setDocumentBatchChatInteractionLoading: (state, action: PayloadAction<boolean>) => {
      state.chatInteractionIsLoading = action.payload
    },
    resetDocumentBatch: () => initialState,
  },
})

export const {
  setDocumentBatchLoading,
  setDocumentBatchDocuments,
  startDocumentBatchPolling,
  stopDocumentBatchPolling,
  setDocumentBatchCreator,
  setDocumentBatchCreatorIsLoading,
  setDocumentBatchContact,
  setDocumentBatchContactIsLoading,
  setDocumentBatchChatResultLoading,
  setDocumentBatchChatResultLoadFailed,
  setDocumentBatchChatAiAgentId,
  setDocumentBatchChatInteractionLoading,
  resetDocumentBatch,
} = documentBatchSlice.actions

export const selectDocumentBatchDocuments = (state: RootState) => state.processDocument.documentBatch.documents

export const selectDocumentBatchCreatedAt = createSelector([selectDocumentBatchDocuments], (documents) =>
  documents.reduce<string | null>((latest, document) => (!latest || document.createdAt > latest ? document.createdAt : latest), null),
)

export const selectDocumentBatchHasMultipleDocuments = createSelector([selectDocumentBatchDocuments], (documents) => documents.length > 1)

export const selectDocumentBatchLoading = (state: RootState) => state.processDocument.documentBatch.isLoading

export const selectDocumentBatchPollingEnabled = (state: RootState) => state.processDocument.documentBatch.pollingEnabled

export const selectDocumentBatchCreator = (state: RootState) => state.processDocument.documentBatch.creator

export const selectDocumentBatchCreatorIsLoading = (state: RootState) => state.processDocument.documentBatch.creatorIsLoading

export const selectDocumentBatchContact = (state: RootState) => state.processDocument.documentBatch.contact

export const selectDocumentBatchContactIsLoading = (state: RootState) => state.processDocument.documentBatch.contactIsLoading

export const selectDocumentBatchChatResultLoading = (state: RootState) => state.processDocument.documentBatch.chatResultIsLoading

export const selectDocumentBatchChatResultLoadFailed = (state: RootState) => state.processDocument.documentBatch.chatResultLoadFailed

export const selectDocumentBatchChatAiAgentId = (state: RootState) => state.processDocument.documentBatch.chatAiAgentId

export const selectDocumentBatchChatInteractionLoading = (state: RootState) => state.processDocument.documentBatch.chatInteractionIsLoading
